'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var jsonschema = require('jsonschema');
var helpers = require('jsonschema/lib/helpers');
var schemaMismatchError = require('./schema-mismatch-error.js');

var BaseBus = /** @class */ (function () {
    function BaseBus() {
        this._subscriptions = new Map();
        this.loggingEnabled = false;
    }
    BaseBus.prototype.enableLogging = function () {
        this.loggingEnabled = true;
    };
    BaseBus.prototype.disableLogging = function () {
        this.loggingEnabled = false;
    };
    /**
     * Register a schema for the specified channel and equality checking on subsequent registers.
     * Subsequent registers must use an equal schema or an error will be thrown.
     * @param channel - name of event channel to register schema to
     * @param schema - all communication on channel must follow this schema
     * @returns returns false if event channel already existed of true if a new one was created
     *
     * @throws {@link SchemaMismatchError}
     * This exception is thrown if new schema does not match already registered schema.
     */
    BaseBus.prototype.register = function (channel, schema) {
        var shouldCreate = !this._subscriptions.has(channel);
        if (shouldCreate) {
            this.initChannel(channel);
        }
        var registered = this.getSchema(channel);
        if (registered && !helpers.deepCompareStrict(registered, schema)) {
            throw new schemaMismatchError.SchemaMismatchError(channel, registered, schema);
        }
        this.setSchema(channel, schema);
        return shouldCreate;
    };
    /**
     * Unregister the schema for the specified channel if channel exists.
     * @param channel - name of event channel to unregister schema from
     * @returns returns true if event channel existed and an existing schema was removed
     */
    BaseBus.prototype.unregister = function (channel) {
        var exists = !!this.getSchema(channel);
        if (exists) {
            delete this._subscriptions.get(channel).__schema;
        }
        return exists;
    };
    /**
     * Get the schema registered on the specified event channel.
     * @param channel - name of the event channel to fetch the schema from
     * @returns the schema or `undefined`
     */
    BaseBus.prototype.getSchema = function (channel) {
        var _a;
        return (_a = this._subscriptions.get(channel)) === null || _a === void 0 ? void 0 : _a.__schema;
    };
    /**
     * Get the latest published payload on the specified event channel.
     * @param channel - name of the event channel to fetch the latest payload from
     * @returns the latest payload or `undefined`
     */
    BaseBus.prototype.getLatest = function (channel) {
        return this.getHistory(channel)[0];
    };
    BaseBus.prototype.log = function () {
        var data = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            data[_i] = arguments[_i];
        }
        if (!this.loggingEnabled) {
            return;
        }
        // eslint-disable-next-line no-console
        console.log.apply(console, data);
    };
    BaseBus.prototype.setSchema = function (channel, schema) {
        if (!this._subscriptions.has(channel)) {
            this.initChannel(channel);
        }
        this._subscriptions.get(channel).__schema = schema;
    };
    BaseBus.prototype.setLatest = function (channel, payload) {
        if (!this._subscriptions.has(channel)) {
            this.initChannel(channel);
        }
        var history = this.getHistory(channel);
        history[1] = history[0];
        history[0] = payload;
    };
    BaseBus.prototype.isDuplicateValueEmitted = function (channel) {
        var history = this.getHistory(channel);
        return (history === null || history === void 0 ? void 0 : history.length) === 2 && history[0] === history[1];
    };
    BaseBus.prototype.initChannel = function (channel) {
        this._subscriptions.set(channel, {
            __payloadsHistory: [],
        });
    };
    BaseBus.prototype.getHistory = function (channel) {
        var _a, _b;
        return (_b = (_a = this._subscriptions.get(channel)) === null || _a === void 0 ? void 0 : _a.__payloadsHistory) !== null && _b !== void 0 ? _b : [];
    };
    BaseBus.prototype.isPayloadInvalid = function (channel, payload) {
        var schema = this.getSchema(channel);
        return (typeof payload !== 'undefined' &&
            !!schema &&
            !jsonschema.validate(payload, schema).valid);
    };
    return BaseBus;
}());

exports.BaseBus = BaseBus;
